import { Component, computed, effect, inject, signal } from '@angular/core';
import { DialogHeaderTitleDirective } from 'common/dialog/dialog-slots.directive';
import { DialogComponent } from 'common/dialog/dialog.component';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonDirective } from 'common/button/button.directive';

export enum ConfirmDialogAction {
  DELETE = 'delete',
  OK = 'ok',
  OK_CANCEL = 'ok_cancel',
}

export type TranslationKey = (typeof TranslationKey)[keyof typeof TranslationKey];

export const TranslationKey = {
  delete: 'general.' + ConfirmDialogAction.DELETE,
  cancel: 'general.cancel',
  save: 'general.save',
  ok: 'general.' + ConfirmDialogAction.OK,
  confirmDeleteHeadline: 'general.confirm.' + ConfirmDialogAction.DELETE + '.headline',
  confirmDeleteMessage: 'general.confirm.' + ConfirmDialogAction.DELETE + '.message',
  confirmOkHeadline: 'general.confirm.' + ConfirmDialogAction.OK + '.headline',
  confirmOkMessage: 'general.confirm.' + ConfirmDialogAction.OK + '.message',
} as const;

export interface ConfirmDialogData {
  readonly confirmDialogAction: ConfirmDialogAction;
  readonly items?: string[];
  readonly customConfirmMessageKey?: string;
  readonly customConfirmMessage?: string;
  readonly customConfirmHeadlineKey?: string;
  readonly customConfirmHeadline?: string;
  readonly customConfirmButtonTitleKey?: string;
  readonly customConfirmButtonTitle?: string;
}

export interface ConfirmDialogButton {
  readonly titleKey: string;
  readonly buttonClass:
    | '-delete'
    | '-primary'
    | '-secondary'
    | '-tertiary'
    | '-save'
    | '-alternative';
}

@Component({
  templateUrl: 'confirm.component.html',
  imports: [
    DialogHeaderTitleDirective,
    DialogComponent,
    CardButtonsComponent,
    TranslateModule,
    NgClass,
    ButtonDirective,
  ],
})
export class ConfirmComponent {
  readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);

  protected maxItemCountToShow = 5;

  headline = computed(() => this.getTranslation(TranslationKey.ok));
  message = computed(() => this.getTranslation(TranslationKey.ok));
  confirmButton = signal<ConfirmDialogButton>({
    titleKey: TranslationKey.ok,
    buttonClass: '-primary',
  });

  readonly confirmDialogAction = computed(
    () => this.data.confirmDialogAction ?? ConfirmDialogAction.OK,
  );
  /**
   * Both `DELETE` and `OK_CANCEL` are cancelable: they show a cancel button
   * next to the confirm button, and the header close icon behaves exactly
   * like cancel (see `dialog-header.component.html`'s `closeIconValue`).
   */
  readonly isCancelable = computed(
    () =>
      this.confirmDialogAction() === ConfirmDialogAction.DELETE ||
      this.confirmDialogAction() === ConfirmDialogAction.OK_CANCEL,
  );
  private readonly dialogRef = inject(MatDialogRef<ConfirmComponent>);

  constructor() {
    effect(() => {
      switch (this.confirmDialogAction()) {
        case 'delete':
          {
            this.headline = signal(this.getTranslation(TranslationKey.confirmDeleteHeadline));
            this.message = signal(this.getTranslation(TranslationKey.confirmDeleteMessage));
            this.confirmButton.set({
              titleKey: TranslationKey.delete,
              buttonClass: '-delete',
            });
          }
          break;
        case 'ok':
        case 'ok_cancel': {
          if (this.isDialogDataCorrect()) {
            return;
          }
          break;
        }
        default: {
          this.headline = signal(this.getTranslation(TranslationKey.ok));
          this.message = signal(this.getTranslation(TranslationKey.ok));
          this.confirmButton.set({
            titleKey: TranslationKey.ok,
            buttonClass: '-primary',
          });
        }
      }
    });
  }

  readonly translateService = inject(TranslateService);
  readonly translations = toSignal(this.translateService.stream(Object.values(TranslationKey)), {
    initialValue: {
      [TranslationKey.delete]: 'general.' + ConfirmDialogAction.DELETE,
      [TranslationKey.cancel]: 'general.cancel',
      [TranslationKey.save]: 'general.save',
      [TranslationKey.ok]: 'general.ok',
    },
  });

  getTranslation(key: TranslationKey) {
    return this.translations()[key];
  }

  onConfirm() {
    this.dialogRef.close(true);
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  isDialogDataCorrect(): boolean {
    if (this.data.customConfirmMessageKey && this.data.customConfirmHeadlineKey) {
      return true;
    }

    if (this.data.customConfirmMessage && this.data.customConfirmHeadline) {
      return true;
    }
    console.log(
      'If ConfirmDialogAction === ' +
        ConfirmDialogAction.OK +
        ', customConfirmMessageKey or customConfirmMessage and customConfirmHeadlineKey or customConfirmHeadline must be set',
    );
    return false;
  }
}
